// Tradelog marketing-page pricing currency library.
//
// This is SEPARATE from `lib/currency.js` (which handles per-account
// trading-account currency inside the app). This file only powers the
// landing / pricing page currency picker so international visitors can
// preview the subscription cost in their local currency.
//
// Base pricing is authored in INR (Razorpay's native currency). All other
// currencies are shown as approximate conversions and prefixed with "≈"
// because the actual charge still happens in INR at checkout.
//
// Rates were snapshot Feb 2026 and hand-rounded to keep the display crisp.

// `rate` = units of the local currency per 1 INR.
export const CURRENCIES = {
  INR: { code: "INR", symbol: "₹",   flag: "🇮🇳", name: "Indian Rupee",       rate: 1,        locale: "en-IN" },
  USD: { code: "USD", symbol: "$",   flag: "🇺🇸", name: "US Dollar",           rate: 0.0120,   locale: "en-US" },
  EUR: { code: "EUR", symbol: "€",   flag: "🇪🇺", name: "Euro",                rate: 0.0111,   locale: "en-IE" },
  GBP: { code: "GBP", symbol: "£",   flag: "🇬🇧", name: "British Pound",       rate: 0.00952,  locale: "en-GB" },
  AUD: { code: "AUD", symbol: "A$",  flag: "🇦🇺", name: "Australian Dollar",   rate: 0.0182,   locale: "en-AU" },
  CAD: { code: "CAD", symbol: "C$",  flag: "🇨🇦", name: "Canadian Dollar",     rate: 0.0164,   locale: "en-CA" },
  SGD: { code: "SGD", symbol: "S$",  flag: "🇸🇬", name: "Singapore Dollar",    rate: 0.0161,   locale: "en-SG" },
  AED: { code: "AED", symbol: "د.إ", flag: "🇦🇪", name: "UAE Dirham",          rate: 0.0442,   locale: "en-AE" },
  SAR: { code: "SAR", symbol: "﷼",   flag: "🇸🇦", name: "Saudi Riyal",         rate: 0.0453,   locale: "en"    },
  JPY: { code: "JPY", symbol: "¥",   flag: "🇯🇵", name: "Japanese Yen",        rate: 1.82,     locale: "ja-JP" },
  CNY: { code: "CNY", symbol: "¥",   flag: "🇨🇳", name: "Chinese Yuan",        rate: 0.087,    locale: "zh-CN" },
  HKD: { code: "HKD", symbol: "HK$", flag: "🇭🇰", name: "Hong Kong Dollar",    rate: 0.0943,   locale: "en-HK" },
  CHF: { code: "CHF", symbol: "CHF ",flag: "🇨🇭", name: "Swiss Franc",         rate: 0.0106,   locale: "de-CH" },
  NZD: { code: "NZD", symbol: "NZ$", flag: "🇳🇿", name: "NZ Dollar",           rate: 0.020,    locale: "en-NZ" },
  ZAR: { code: "ZAR", symbol: "R",   flag: "🇿🇦", name: "South African Rand",  rate: 0.222,    locale: "en-ZA" },
  BRL: { code: "BRL", symbol: "R$",  flag: "🇧🇷", name: "Brazilian Real",      rate: 0.0606,   locale: "pt-BR" },
  MXN: { code: "MXN", symbol: "Mex$",flag: "🇲🇽", name: "Mexican Peso",        rate: 0.204,    locale: "es-MX" },
  TRY: { code: "TRY", symbol: "₺",   flag: "🇹🇷", name: "Turkish Lira",        rate: 0.385,    locale: "tr-TR" },
  NGN: { code: "NGN", symbol: "₦",   flag: "🇳🇬", name: "Nigerian Naira",      rate: 17.86,    locale: "en-NG" },
  PKR: { code: "PKR", symbol: "₨",   flag: "🇵🇰", name: "Pakistani Rupee",     rate: 3.33,     locale: "en-PK" },
  IDR: { code: "IDR", symbol: "Rp",  flag: "🇮🇩", name: "Indonesian Rupiah",   rate: 188,      locale: "id-ID" },
  MYR: { code: "MYR", symbol: "RM",  flag: "🇲🇾", name: "Malaysian Ringgit",   rate: 0.0529,   locale: "ms-MY" },
  THB: { code: "THB", symbol: "฿",   flag: "🇹🇭", name: "Thai Baht",           rate: 0.425,    locale: "th-TH" },
  PHP: { code: "PHP", symbol: "₱",   flag: "🇵🇭", name: "Philippine Peso",     rate: 0.68,     locale: "en-PH" },
  VND: { code: "VND", symbol: "₫",   flag: "🇻🇳", name: "Vietnamese Dong",     rate: 294,      locale: "vi-VN" },
};

export const CURRENCY_ORDER = [
  "INR", "USD", "EUR", "GBP", "AUD", "CAD", "SGD", "AED", "SAR",
  "JPY", "CNY", "HKD", "CHF", "NZD", "ZAR", "BRL", "MXN", "TRY",
  "NGN", "PKR", "IDR", "MYR", "THB", "PHP", "VND",
];

// Round to a nice display value based on magnitude so we never render
// weirdly precise conversions like `$7.23`.
function niceRound(raw) {
  if (raw < 10)    return Math.round(raw * 2) / 2;      // .5 precision
  if (raw < 100)   return Math.round(raw);              // 1s
  if (raw < 1000)  return Math.round(raw / 10) * 10;    // 10s
  if (raw < 10000) return Math.round(raw / 100) * 100;  // 100s
  return Math.round(raw / 1000) * 1000;                 // 1000s
}

// Format a price authored in INR into the target currency.
export function formatPrice(baseInr, ccyCode) {
  const c = CURRENCIES[ccyCode] || CURRENCIES.INR;
  const raw = baseInr * c.rate;
  const rounded = niceRound(raw);
  const noDecimal = ["JPY", "VND", "IDR", "KRW"].includes(c.code);
  const formatted = noDecimal
    ? Math.round(rounded).toLocaleString(c.locale)
    : rounded.toLocaleString(c.locale, {
        maximumFractionDigits: rounded < 10 ? 2 : 0,
      });
  const prefix = c.code === "INR" ? "" : "≈ ";
  return `${prefix}${c.symbol}${formatted}`;
}

// Detect a sensible default currency from browser locale + timezone.
export function detectCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const tzMap = {
      "Asia/Kolkata": "INR", "Asia/Calcutta": "INR",
      "Asia/Karachi": "PKR", "Asia/Dubai": "AED",
      "Asia/Riyadh": "SAR", "Asia/Singapore": "SGD",
      "Asia/Tokyo": "JPY", "Asia/Shanghai": "CNY",
      "Asia/Hong_Kong": "HKD", "Asia/Jakarta": "IDR",
      "Asia/Kuala_Lumpur": "MYR", "Asia/Bangkok": "THB",
      "Asia/Manila": "PHP", "Asia/Ho_Chi_Minh": "VND",
      "Europe/London": "GBP", "Europe/Zurich": "CHF",
      "Europe/Istanbul": "TRY",
      "Australia/Sydney": "AUD", "Pacific/Auckland": "NZD",
      "America/Toronto": "CAD", "America/Vancouver": "CAD",
      "America/Sao_Paulo": "BRL", "America/Mexico_City": "MXN",
      "Africa/Johannesburg": "ZAR", "Africa/Lagos": "NGN",
    };
    if (tzMap[tz]) return tzMap[tz];
    const eurCities = ["Paris","Berlin","Madrid","Rome","Amsterdam","Brussels","Vienna","Dublin","Lisbon","Helsinki","Athens","Warsaw"];
    if (eurCities.some(c => tz.includes(c))) return "EUR";
    if (tz.startsWith("America/")) return "USD";
    const lang = (typeof navigator !== "undefined" ? (navigator.language || "en-IN") : "en-IN").toLowerCase();
    if (lang.endsWith("-in")) return "INR";
    if (lang.endsWith("-gb")) return "GBP";
    if (lang.endsWith("-au")) return "AUD";
    if (lang.endsWith("-ca")) return "CAD";
    if (lang.endsWith("-sg")) return "SGD";
  } catch (_) { /* no-op */ }
  return "INR";
}
